import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Search, UserPlusIcon } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'

import { Badge, Button, Checkbox, FilterPillButton, FiltersDropdown, Pagination, Table } from 'slab'
import { useGetPartnersPartnerIdPartnerAdmins as useGetPartnerTeamMembers } from 'utils/web_api'
import { type PartnersPartnerAdmin } from 'utils/web_api.schemas'

import InvitePartnerAdminModal from './InvitePartnerAdminModal'
import PartnersTeamMemberPanel from './PartnersTeamMemberPanel'
import PartnersTeamMembersTableLoadingSkeleton from './PartnersTeamMembersTableLoadingSkeleton'
import { capitalize } from '../../utils'

// Available filter options based on PartnerAdmin model
const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'producer', label: 'Producer' },
  { value: 'finance', label: 'Finance' },
]

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
]

const columns: ColumnDef<PartnersPartnerAdmin>[] = [
  {
    id: 'name',
    header: 'Name',
    accessorFn: (row) => `${row.first_name} ${row.last_name}`,
    cell: ({ row }) => {
      return <Table.Cell>{`${row.original.first_name} ${row.original.last_name}`}</Table.Cell>
    },
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => {
      return <Table.Cell>{row.original.email}</Table.Cell>
    },
  },
  {
    accessorKey: 'roles',
    header: 'Role(s)',
    cell: ({ row }) => {
      const massagedRoles = row.original.roles.map((role: string) => {
        return capitalize(role)
      })

      return <Table.Cell>{massagedRoles.join(', ')}</Table.Cell>
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('status') as string
      return (
        <Table.Cell>
          <Badge type={status === 'active' ? 'success' : 'info'}>{capitalize(status)}</Badge>
        </Table.Cell>
      )
    },
  },
  {
    accessorKey: 'carrier_partnerships',
    header: 'Carrier Partnerships',
    cell: ({ row }) => {
      const partnerships = row.original.carrier_partnerships ?? []
      if (partnerships.length === 0) {
        return <Table.Cell><span className='text-gray-400'>None</span></Table.Cell>
      }
      return (
        <Table.Cell>
          {partnerships.map(p => p.display_name).join(', ')}
        </Table.Cell>
      )
    },
  },
]

function PartnersTeamMembersTable (
  { partnerId }:
  { partnerId: string }
) {
  const [rowSelection, setRowSelection] = useState({})
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set())
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize] = useState(10)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [selectedTeamMember, setSelectedTeamMember] = useState<PartnersPartnerAdmin | null>(null)
  const [isTeamMemberPanelOpen, setIsTeamMemberPanelOpen] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    setPageIndex(0)
    setIsInitialLoad(false)
  }, [debouncedSearchTerm])

  const filterParams = useMemo(() => {
    const params: Record<string, any> = {}

    if (debouncedSearchTerm) {
      params['filter[name_or_email]'] = debouncedSearchTerm
    }

    if (selectedRoles.size > 0) {
      params['filter[roles][]'] = Array.from(selectedRoles)
    }

    if (selectedStatuses.size > 0 && selectedStatuses.size < STATUS_OPTIONS.length) {
      // Only apply status filter if not all statuses are selected
      // If all statuses are selected, don't apply any status filter (show all)
      params['filter[status]'] = Array.from(selectedStatuses)[0]
    }

    params['page[number]'] = pageIndex + 1
    params['page[size]'] = pageSize

    return params
  }, [debouncedSearchTerm, selectedRoles, selectedStatuses, pageIndex, pageSize])

  const { data: partnerTeamMembersData, isLoading } = useGetPartnerTeamMembers(partnerId, filterParams)

  const data = partnerTeamMembersData?.data || []
  const totalRows = partnerTeamMembersData?.pagination?.total_records || 0

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const newPagination = updater({ pageIndex, pageSize })
        setPageIndex(newPagination.pageIndex)
      } else {
        setPageIndex(updater.pageIndex)
      }
    },
    pageCount: Math.ceil(totalRows / pageSize),
    manualPagination: true,
  })

  const toggleRole = (role: string) => {
    setSelectedRoles(prev => {
      const next = new Set(prev)
      if (next.has(role)) {
        next.delete(role)
      } else {
        next.add(role)
      }
      return next
    })
    setPageIndex(0)
    setIsInitialLoad(false)
  }

  const toggleStatus = (status: string) => {
    setSelectedStatuses(prev => {
      const next = new Set(prev)
      if (next.has(status)) {
        next.delete(status)
      } else {
        next.add(status)
      }
      return next
    })
    setPageIndex(0)
    setIsInitialLoad(false)
  }

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    if (value !== debouncedSearchTerm) {
      setPageIndex(0)
    }
  }

  const handleTeamMemberClick = (teamMember: PartnersPartnerAdmin) => {
    setSelectedTeamMember(teamMember)
    setIsTeamMemberPanelOpen(true)
  }

  const handleCloseTeamMemberPanel = () => {
    setIsTeamMemberPanelOpen(false)
    setSelectedTeamMember(null)
  }

  if (isLoading && isInitialLoad) return <PartnersTeamMembersTableLoadingSkeleton columns={columns} />

  return (
    <div className='bg-white rounded-2xl shadow-sm border border-gray-200'>
      {/* Search and Filters */}
      <div className='p-6 space-y-4'>
        <div className='flex gap-3 items-center'>
          <div className='relative flex-1'>
            <div className='absolute inset-y-0 left-0 flex items-center pl-3'>
              <Search size={16} className='text-gray-400' />
            </div>
            <input
              id='search-team-members'
              placeholder='Find a team member'
              value={searchTerm}
              onChange={(event) => {
                handleSearchChange(event.target.value)
              }}
              className='w-full md:w-2/3 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-thatch-500 focus:border-thatch-500'
            />
          </div>
          <Button
            leftIcon={UserPlusIcon}
            size='large'
            variant='primary'
            onClick={() => setIsInviteModalOpen(true)}
          >
            Invite team member
          </Button>
        </div>

        {/* Filter Pills */}
        <div className='flex gap-3'>
          <FiltersDropdown
            trigger={({ open }) => (
              <FilterPillButton
                label='Role'
                count={selectedRoles.size}
                open={open}
              />
            )}
          >
            <FiltersDropdown.Filters>
              {ROLE_OPTIONS.map((option) => (
                <Checkbox
                  key={option.value}
                  checked={selectedRoles.has(option.value)}
                  onChange={() => toggleRole(option.value)}
                  value={option.value}
                  label={option.label}
                />
              ))}
            </FiltersDropdown.Filters>
          </FiltersDropdown>

          <FiltersDropdown
            trigger={({ open }) => (
              <FilterPillButton
                label='Status'
                count={selectedStatuses.size}
                open={open}
              />
            )}
          >
            <FiltersDropdown.Filters>
              {STATUS_OPTIONS.map((option) => (
                <Checkbox
                  key={option.value}
                  checked={selectedStatuses.has(option.value)}
                  onChange={() => toggleStatus(option.value)}
                  value={option.value}
                  label={option.label}
                />
              ))}
            </FiltersDropdown.Filters>
          </FiltersDropdown>
        </div>
      </div>
      {/* Table */}
      <Table>
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <Table.ColumnHeader key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </Table.ColumnHeader>
                )
              })}
            </Table.Row>
          ))}
        </Table.Head>
        <Table.Body>
          {isLoading && !isInitialLoad && (
            <Table.Row>
              <Table.Cell colSpan={columns.length} align='center'>
                <div />
              </Table.Cell>
            </Table.Row>
          )}
          {!isLoading && data.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={columns.length} align='center'>
                {debouncedSearchTerm || selectedRoles.size > 0 || (selectedStatuses.size > 0 && selectedStatuses.size < STATUS_OPTIONS.length)
                  ? 'No team members found.'
                  : "You don't have any team members yet."}
              </Table.Cell>
            </Table.Row>
          )}
          {!isLoading && table.getRowModel().rows.map((row) => (
            <Table.Row
              key={row.id}
              accessibleLabel={`View details for ${row.original.first_name} ${row.original.last_name}`}
              onClick={() => handleTeamMemberClick(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <React.Fragment key={cell.id}>
                  {flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                  )}
                </React.Fragment>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {/* Pagination */}
      <div className='flex justify-end py-2'>
        {totalRows > 0 && (
          <Pagination
            pageIndex={pageIndex}
            perPage={pageSize}
            totalRows={totalRows}
            onPageChange={(newPageIndex) => setPageIndex(newPageIndex)}
          />
        )}
      </div>

      <InvitePartnerAdminModal
        partnerId={partnerId}
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
      />

      <PartnersTeamMemberPanel
        teamMember={selectedTeamMember}
        isOpen={isTeamMemberPanelOpen}
        onClose={handleCloseTeamMemberPanel}
        partnerId={partnerId}
      />
    </div>
  )
}

export default PartnersTeamMembersTable
